import {
  ImagePlus,
  FileText,
  Settings,
  Film,
  Lightbulb,
  Download,
  ExternalLink,
  Sparkles,
} from 'lucide-react';

export function UsageGuideContent() {
  const steps = [
    {
      icon: ImagePlus,
      title: '画像を追加する',
      description:
        '動画に使用する画像をドラッグ&ドロップ、またはクリックしてアップロードします。複数の画像を一度に追加でき、追加後はドラッグで順番を入れ替えられます。',
    },
    {
      icon: FileText,
      title: 'シーンテキストを入力する',
      description:
        '画面左のテキストエリアに、各シーンのテキストを1行ずつ入力します。1行目が1枚目の画像、2行目が2枚目の画像...と対応します。',
    },
    {
      icon: Settings,
      title: 'APIキーを設定する',
      description:
        '左上のメニューの「API設定」から、ElevenLabs APIの設定を行います。',
      extra: (
        <div className="mt-3 p-3 bg-warm-50 rounded-xl text-sm space-y-2">
          <p className="font-medium text-warm-700">
            ElevenLabs APIキーの取得方法:
          </p>
          <ol className="list-decimal list-inside text-warm-600 space-y-1 text-xs">
            <li>
              <a
                href="https://elevenlabs.io/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-coral-600 hover:underline inline-flex items-center gap-1"
              >
                ElevenLabs <ExternalLink size={10} />
              </a>
              でアカウント作成
            </li>
            <li>ダッシュボードからAPIキーをコピー</li>
            <li>設定画面に貼り付け</li>
          </ol>
        </div>
      ),
    },
    {
      icon: Film,
      title: '動画を生成する',
      description: '画面左下のボタンで動画を生成します。',
      extra: (
        <ul className="mt-2 text-xs text-warm-500 space-y-1">
          <li>
            <span className="font-medium text-warm-600">
              音声+動画生成:
            </span>{' '}
            一括で全ての処理を実行
          </li>
          <li>
            <span className="font-medium text-warm-600">
              音声のみ生成:
            </span>{' '}
            テキストから音声だけを生成
          </li>
          <li>
            <span className="font-medium text-warm-600">
              動画のみ生成:
            </span>{' '}
            既存の音声で動画を作成
          </li>
        </ul>
      ),
    },
    {
      icon: Lightbulb,
      title: '待ち時間を有効活用',
      description:
        '動画生成には数分かかります。その間に次の作業を進めましょう。',
      extra: (
        <p className="mt-2 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded inline-block">
          生成中はこのタブを閉じないでください
        </p>
      ),
    },
    {
      icon: Download,
      title: '受け取る',
      description:
        '動画生成が完了すると保存ボタンが有効になります。パソコンでは「動画DL」でMP4形式の動画がダウンロードされ、スマートフォンでは「保存」で共有シートが開き、写真アプリやクラウドなど保存先を選べます。',
    },
  ];

  return (
    <div className="space-y-5">
      {steps.map((step, index) => (
        <section key={index} className="flex gap-4">
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 rounded-xl bg-coral-100 flex items-center justify-center shrink-0">
              <step.icon size={20} className="text-coral-600" />
            </div>
            {index < steps.length - 1 && (
              <div className="w-0.5 flex-1 bg-coral-100 mt-2" />
            )}
          </div>
          <div className="flex-1 pb-4">
            <h3 className="font-display font-semibold text-warm-800 mb-1">
              {index + 1}. {step.title}
            </h3>
            <p className="text-sm text-warm-600 leading-relaxed">
              {step.description}
            </p>
            {step.extra}
          </div>
        </section>
      ))}

      {/* Tips */}
      <section className="bg-linear-to-br from-coral-50 to-coral-100 p-4 rounded-xl border border-coral-200">
        <h3 className="font-display font-semibold text-coral-800 mb-2 flex items-center gap-2">
          <Sparkles size={16} />
          便利な機能
        </h3>
        <ul className="text-sm text-coral-700 space-y-1.5">
          <li className="flex items-start gap-2">
            <span className="w-1 h-1 rounded-full bg-coral-400 mt-2 shrink-0" />
            各シーンの音声前後に「間」を追加できます
          </li>
          <li className="flex items-start gap-2">
            <span className="w-1 h-1 rounded-full bg-coral-400 mt-2 shrink-0" />
            トランジション効果や効果音を設定で追加可能
          </li>
          <li className="flex items-start gap-2">
            <span className="w-1 h-1 rounded-full bg-coral-400 mt-2 shrink-0" />
            個別シーンの音声だけを再生成することもできます
          </li>
          <li className="flex items-start gap-2">
            <span className="w-1 h-1 rounded-full bg-coral-400 mt-2 shrink-0" />
            音声ファイルをアップロードして使用することも可能
          </li>
        </ul>
      </section>
    </div>
  );
}
